import React, { useEffect, useState } from 'react';
import { Card, Modal, Form, Alert } from 'react-bootstrap';
import { FiPlay, FiRefreshCw, FiAlertTriangle, FiServer } from 'react-icons/fi';
import { TbFlask } from 'react-icons/tb';
import { FiClipboard } from 'react-icons/fi';
import { testDataApi, interpretationsApi } from '../services/api';
import { PendingPatient, PendingTestDataResponse, TestDataItem } from '../types';
import { LoadingSpinner, SLAIndicator, EmptyState, Pagination } from '../components/common';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const PAGE_SIZE = 15;

const TestDataPending: React.FC = () => {
  const [data, setData] = useState<PendingTestDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PendingPatient | null>(null);
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination (client-side for now)
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await testDataApi.getPending();
      setData(response);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching pending test data:', error);
      setError('Error al cargar los datos pendientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleGenerateClick = (patient: PendingPatient) => {
    setSelectedPatient(patient);
    setSelectedTests(patient.tests.map((t) => t.test_data_id));
    setShowModal(true);
  };

  const handleGenerate = async () => {
    if (!selectedPatient) return;
    setGenerating(selectedPatient.patient_key);
    setError(null);

    try {
      await interpretationsApi.generate({
        patient_key: selectedPatient.patient_key,
        test_data_ids: selectedTests,
      });
      setSuccess(
        `Interpretación iniciada para ${selectedPatient.patient_name}. Será procesada en breve.`
      );
      setShowModal(false);
      await fetchData();
    } catch (error) {
      console.error('Error generating interpretation:', error);
      setError('Error al iniciar la generación de interpretación');
    } finally {
      setGenerating(null);
    }
  };

  const toggleTestSelection = (testId: string) => {
    setSelectedTests((prev) =>
      prev.includes(testId)
        ? prev.filter((id) => id !== testId)
        : [...prev, testId]
    );
  };

  const getTestTypeCounts = (tests: { test_type: string }[]) => {
    const labCount = tests.filter(t => t.test_type === 'LAB_RESULTS').length;
    const questionnaireCount = tests.filter(t => t.test_type !== 'LAB_RESULTS').length;
    return { labCount, questionnaireCount };
  };

  const getUniqueSources = (tests: TestDataItem[]): string[] => {
    return Array.from(new Set(tests.map(t => t.source_system)));
  };

  const formatSourceName = (source: string): string => {
    // Normalize and format source name
    // Handle snake_case, CAPS, and mixed case issues
    return source
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
      .replace(/\bAuditoria\b/gi, 'Auditoría'); // Fix common accent issues
  };

  // Pagination
  const totalItems = data?.patients.length || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const paginatedPatients = data?.patients.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  ) || [];

  if (loading) {
    return <LoadingSpinner message="Cargando datos pendientes..." />;
  }

  return (
    <div className="test-data-page">
      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="alert-toast">
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="alert-toast">
          {success}
        </Alert>
      )}

      {/* Header Bar */}
      <div className="page-header-bar">
        <div className="header-info">
          <span className="header-stat">
            <strong>{data?.total_patients || 0}</strong> pacientes
          </span>
          <span className="header-divider">·</span>
          <span className="header-stat">
            <strong>{data?.total_tests || 0}</strong> exámenes
          </span>
        </div>
        <button className="refresh-btn" onClick={fetchData} disabled={loading}>
          <FiRefreshCw size={14} />
          <span>Actualizar</span>
        </button>
      </div>

      {/* List */}
      <Card className="data-list-card">
        {!data || data.patients.length === 0 ? (
          <Card.Body>
            <EmptyState
              title="No hay datos pendientes"
              description="Todos los exámenes han sido interpretados"
            />
          </Card.Body>
        ) : (
          <div className="data-list">
            {/* Header */}
            <div className="list-header">
              <span className="col-patient">Paciente</span>
              <span className="col-company">Empresa</span>
              <span className="col-source">Origen</span>
              <span className="col-tests">Exámenes</span>
              <span className="col-date">Recibido</span>
              <span className="col-sla">SLA</span>
              <span className="col-action"></span>
            </div>

            {/* Rows */}
            <div className="list-body">
              {paginatedPatients.map((patient) => {
                const testCounts = getTestTypeCounts(patient.tests);
                const sources = getUniqueSources(patient.tests);
                const isGenerating = generating === patient.patient_key;

                return (
                  <div
                    key={patient.patient_key}
                    className={`list-row ${patient.sla_exceeded ? 'sla-exceeded' : ''}`}
                  >
                    <span className="col-patient">
                      <span className="patient-name">{patient.patient_name}</span>
                      <span className="patient-key">{patient.patient_key}</span>
                    </span>

                    <span className="col-company">
                      {patient.company_id || '-'}
                    </span>

                    <span className="col-source">
                      {sources.map((source, idx) => (
                        <span key={source} className="source-badge" title={source}>
                          <FiServer size={11} />
                          {formatSourceName(source)}
                        </span>
                      ))}
                    </span>

                    <span className="col-tests">
                      {testCounts.labCount > 0 && (
                        <span className="test-badge lab">
                          <TbFlask size={13} />
                          {testCounts.labCount}
                        </span>
                      )}
                      {testCounts.questionnaireCount > 0 && (
                        <span className="test-badge quest">
                          <FiClipboard size={12} />
                          {testCounts.questionnaireCount}
                        </span>
                      )}
                    </span>

                    <span className="col-date">
                      {format(new Date(patient.oldest_received_at), 'dd MMM, HH:mm', { locale: es })}
                    </span>

                    <span className="col-sla">
                      <SLAIndicator
                        hoursRemaining={patient.hours_remaining}
                        slaExceeded={patient.sla_exceeded}
                      />
                    </span>

                    <span className="col-action">
                      <button
                        className={`interpret-action-btn ${patient.sla_exceeded ? 'urgent' : ''}`}
                        onClick={() => handleGenerateClick(patient)}
                        disabled={isGenerating}
                      >
                        {isGenerating ? (
                          <span className="loading-dots">...</span>
                        ) : (
                          <FiPlay size={12} />
                        )}
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
              loading={loading}
            />
          </div>
        )}
      </Card>

      {/* Generate Interpretation Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>Generar Interpretación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPatient && (
            <>
              <div className="modal-patient-info">
                <div className="patient-avatar">
                  {selectedPatient.patient_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h6 className="patient-name">{selectedPatient.patient_name}</h6>
                  <span className="patient-key">{selectedPatient.patient_key}</span>
                </div>
              </div>

              <div className="modal-section">
                <h6 className="section-title">Seleccionar exámenes</h6>
                <p className="section-description">
                  Selecciona los exámenes que deseas incluir en la interpretación consolidada.
                </p>
              </div>

              <div className="test-selection-list">
                {selectedPatient.tests.map((test) => (
                  <div
                    key={test.test_data_id}
                    className={`test-selection-item ${
                      selectedTests.includes(test.test_data_id) ? 'selected' : ''
                    }`}
                    onClick={() => toggleTestSelection(test.test_data_id)}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={selectedTests.includes(test.test_data_id)}
                      onChange={() => toggleTestSelection(test.test_data_id)}
                      className="test-checkbox"
                    />
                    <div className="test-info">
                      <span className="test-type">
                        {test.test_type === 'LAB_RESULTS' ? (
                          <><TbFlask size={14} /> Laboratorio</>
                        ) : (
                          <><FiClipboard size={13} /> {test.test_type}</>
                        )}
                      </span>
                      <span className="test-meta">
                        {test.source_system} · {format(new Date(test.received_at), 'dd MMM, HH:mm', { locale: es })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {selectedPatient.sla_exceeded && (
                <div className="sla-warning">
                  <FiAlertTriangle size={16} />
                  <span>Este paciente ha excedido el SLA. Se recomienda procesar inmediatamente.</span>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer-custom">
          <button className="btn-cancel" onClick={() => setShowModal(false)}>
            Cancelar
          </button>
          <button
            className="btn-generate"
            onClick={handleGenerate}
            disabled={selectedTests.length === 0 || generating !== null}
          >
            {generating ? 'Generando...' : `Generar (${selectedTests.length})`}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TestDataPending;
